import React, { useRef } from 'react';
import { isAndroid, isMobile, isTablet } from 'react-device-detect';
import styled, { css } from 'styled-components';
import { isNotStandaloneMobilePWA, isStandalonePWA } from '../../../utils/mobileSupported';
import Button from '../../../components/Button/Button';
import { ASSET_KEY, assetSource } from 'shared/utils/assetSource';
import { getText, TEXT_KEY } from 'shared/utils/localization';
import BattlesReward from './BattlesReward';
import BattlesRankingTable from './BattlesRankingTable';
import BattlesGoal from './BattlesGoal';
import { BUTTON_STROKES } from 'shared/utils/buttonTextStrokes';

const BattlesRoom = props => {
  const ref = useRef(null);
  const { battlesAssets, battlesStarted, isPlayerJoining, handleBattlesGameStart, hasReward, isStartingBattle } = props;
  const disableButton = isStartingBattle || (isPlayerJoining && !hasReward);
  return (
    <Container source={battlesAssets.BATTLES_BACKGROUND_MAIN} battlesStarted={battlesStarted}>
      <LeaderboardContainer ref={ref} source={battlesAssets.BATTLES_LEADERBOARD}>
        <BattlesRankingTable scaleRef={ref} {...props} />
      </LeaderboardContainer>
      <RankContainer>
        {isPlayerJoining ? <BattlesReward {...props} /> : <BattlesGoal {...props} />}

        <BattlesBeginButton
          label={hasReward ? getText(TEXT_KEY.COLLECT_BUTTON) : getText(TEXT_KEY.LET_THE_BATTLES_BEGIN)}
          imageSource={
            !disableButton
              ? assetSource(ASSET_KEY.ROUNDEDRECT_PRIMARY_WIDE)
              : assetSource(ASSET_KEY.ROUNDEDRECT_DISABLED_WIDE)
          }
          onClick={handleBattlesGameStart}
          disabled={disableButton}
        />
      </RankContainer>
    </Container>
  );
};

export default BattlesRoom;

const isMobileBrowser = isMobile && isNotStandaloneMobilePWA && !isTablet;
const isMobilePwa = isStandalonePWA && !isTablet;

const Container = styled.div.withConfig({
  shouldForwardProp: (prop) => !['battlesStarted', 'source'].includes(prop),
})`
  display: ${({ battlesStarted }) => (battlesStarted ? 'flex' : 'none')};
  flex-direction: row;
  width: 100vw;
  height: 100%;
  justify-content: center;
  align-items: flex-end;
  background-image: url(${({ source }) => source});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1;
  position: relative;
  overflow: hidden;
`;

const LeaderboardContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['source'].includes(prop),
})`
  display: flex;
  flex-direction: column;
  aspect-ratio: 9 / 16;
  width: ${isMobilePwa ? (isAndroid ? 60 : 55) : 60}vw;
  height: ${isMobileBrowser ? 94 : 100}%;
  justify-content: center;
  align-items: center;
  background-image: url(${({ source }) => source});
  background-size: contain;
  background-position: center;
  align-self: center;
  background-repeat: no-repeat;
  box-sizing: border-box;
  z-index: 1;
  position: relative;
  padding: 0 8vw 0 8vw;

  ${({ theme }) => theme.screens.device('mobile-browser') && css`
      padding: ${theme.platform('android') ? '0 10vw' : '0 14vw'};
      margin-top: ${theme.platform('android') ? '30px' : '10px'};
    `}
  ${({ theme }) => theme.screens.devices(['tablet-standalone', 'tablet-browser']) && 'padding: 0 7vw 0 7vw;'}
`;

const RankContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: ${isMobilePwa ? 45 : isMobileBrowser ? 40 : 35}vw;
  height: 100%;
  justify-content: flex-end;
  align-items: center;
  z-index: 1;
  padding: ${isMobilePwa ? '8vh 1.5vw 5vh 1.5vw' : '2.5vw 1vw'};

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: 10vw 1vw;
  }
`;

const BattlesBeginButton = styled(Button).attrs(({ disabled }) => ({
  containerStyle: `
    margin-bottom: ${isMobile && !isTablet ? '-1vh' : 'unset'};
    width: ${isMobileBrowser ? 22.7 : 32.5}vw;
    `,
  imageStyle: `
    width: ${isMobileBrowser ? 22.7 : 32.5}vw;
    `,
  textStyle: css`
    font-size: ${isMobileBrowser ? 1.65 : 2.2}vw;
    font-weight: 800;
    text-stroke: 2px ${disabled ? BUTTON_STROKES.DISABLED : '#038500'};
    -webkit-text-stroke: 2px ${disabled ? BUTTON_STROKES.DISABLED : '#038500'};
    text-transform: uppercase;
    ${({ theme }) => theme.BattlesBegin.ButtonTextStyle}
  `,
}))``;

